import axios from 'axios'
import * as ActionType from './types'
import { hideAPIErrorInfo, showAPIErrorInfo } from './errorActions'
import { apiEndpoints, entryPointId } from 'settings/app/constants'
import navigatorAPI from 'settings/xhr/navigatorAPI'
import { getQsrIdentifierArray } from './compareActions'
import { getNumberOfBookmarks } from 'settings/app/helpers'
import {
  bookmarksSearchContextHasChanged,
  CHECK_BOOKMARK_CONTEXT,
} from 'containers/Bookmarks/helpers/helpers'
import { getSearchGetParams } from 'settings/app/urlHelpers'

/**
 *  check if bookmark should be ADDED (For Use in a result list)
 *        if is new search and searchKey od searchType differ from current search
 *        => overlay is shown (conditions down)
 *        => otherwise default toggle bookmark
 *
 * if bookmark should be removed:
 *        ActionType.REMOVE_BOOKMARK_FROM_DETAILS
 *
 * @param bookmark
 * @returns {(function(*, *): Promise<boolean|undefined>)|*}
 */
export const toggleBookmark =
  ({ id, type, searchParams, reset = false }) =>
  async (dispatch, getState) => {
    const state = getState()
    const clonedBookmark = { type, id: id.toString(), searchParams }
    const bookmarkTypeState = state.bookmarks.types[type]
    const isOverlayShown = state.bookmarks.overlay.show

    /**
     * overlay conditions
     * 1. bookmark should have always the same search context (currently only doctors wont have this condition)
     * 2. bookmark list reset (when user accept overlay context)
     * 3. new bookmark did not exists in bookmark state before
     * 4. new bookmark has different search context
     * 5. bookmark state is not empty
     * @type {boolean}
     */
    const { isNewSearchBookmark } = bookmarksSearchContextHasChanged({
      newBookmark: clonedBookmark,
      bookmarkState: bookmarkTypeState,
    })
    const bookmarkDoNotExist = !bookmarkTypeState.items.includes(
      clonedBookmark.id
    )

    const showBookmarkOverlay =
      CHECK_BOOKMARK_CONTEXT.includes(type) &&
      !reset &&
      bookmarkDoNotExist &&
      isNewSearchBookmark &&
      bookmarkTypeState.count > 0

    /**
     * if bookmark should be added with different searchKey or searchType => show overlay
     * if overlay is accepted and and list should be reset 'reset=true' with same parameters
     * and more than 0 items are bookmarked
     */
    if (showBookmarkOverlay) {
      dispatch({
        type: ActionType.SHOW_BOOKMARK_OVERLAY,
        payload: clonedBookmark,
      })
    } else {
      // default bookmark toggling
      dispatch({
        type: ActionType.TOGGLE_BOOKMARK,
        payload: {
          id: clonedBookmark.id,
          type,
          reset,
          bookmarkParams: searchParams,
          totalCount: reset
            ? getNumberOfBookmarks({
                bookmarkState: getState().bookmarks.types,
                excludeBookmarkTyp: type,
              }) + 1
            : bookmarkDoNotExist
            ? getNumberOfBookmarks({
                bookmarkState: getState().bookmarks.types,
              }) + 1
            : getNumberOfBookmarks({
                bookmarkState: getState().bookmarks.types,
              }) - 1,
        },
      })
      if (isOverlayShown) {
        dispatch({
          type: ActionType.HIDE_BOOKMARK_OVERLAY,
        })
      }
    }
  }

/**
 * no searchKey compare is required on bookmarks && compare view
 * @param id
 * @param type
 * @param actionLocation
 * @returns {(function(*, *): Promise<void>)|*}
 */
export const removeBookmark =
  ({ id, type, actionLocation = '' }) =>
  async (dispatch, getState) => {
    // get current bookmark ids && remove id from array
    let typedBookmarkIds = getState().bookmarks.types[type].items.filter(
      (bookedId) => bookedId !== id.toString()
    )

    // remove id from items array
    dispatch({
      type: ActionType.REMOVE_ONE_BOOKMARK,
      payload: {
        type,
        items: typedBookmarkIds,
        totalCount:
          getNumberOfBookmarks({ bookmarkState: getState().bookmarks.types }) -
          1,
      },
    })

    // removed details data for the result if the current id is represented at bookmarkDetails
    // case result list without visiting bookmark view => results do not exists
    if (getState().bookmarks.selectedDetails !== null) {
      const bookMarkDetailResults =
        getState().bookmarks.selectedDetails?.results
      const withOutIdBookMarkDetailResults = bookMarkDetailResults.filter(
        (result) =>
          result.id !== (type === entryPointId.DOCTORS ? parseInt(id, 10) : id)
      ) // remove results

      if (bookMarkDetailResults.length !== withOutIdBookMarkDetailResults) {
        dispatch({
          type: ActionType.REMOVE_BOOKMARK_FROM_DETAILS,
          payload: type === entryPointId.DOCTORS ? parseInt(id, 10) : id,
        })
      }
    }

    // trigger state change @ compare->activeElements
    if (actionLocation === 'compareView') {
      // remove toggle id from array and append next id
      const newActiveIds = getState().compare.activeElements.activeIds.filter(
        (item) => item !== id
      )
      // result data (some components require this data for data mapping)
      const curResultArray = getState().compare.types[type].results

      // cast all string ids into numbers
      if (type === entryPointId.DOCTORS) {
        typedBookmarkIds = typedBookmarkIds.map((id) => parseInt(id, 10))
      }

      // get diff between bookmark items and active ids
      const diffIdArray = typedBookmarkIds.filter(
        (x) => !newActiveIds.includes(x)
      )

      if (diffIdArray.length > 0) {
        newActiveIds.push(diffIdArray[0])
      }

      dispatch({
        type: ActionType.REMOVE_ONE_RESULT_ITEM,
        payload: {
          resultTyp: type,
          results: curResultArray.filter((result) => result.id !== id),
        },
      })

      dispatch({
        type: ActionType.TOGGLE_ACTIVE_COMPARE_ITEMS,
        payload: { activeElements: { type, activeIds: newActiveIds } },
      })
    }
  }

export const clearBookmarkDetails = () => ({
  type: ActionType.CLEAR_BOOKMARK_DETAILS,
})

export const removeBookmarkFromSelectedDetails = (id) => ({
  type: ActionType.REMOVE_BOOKMARK_FROM_DETAILS,
  payload: id,
})

export const hideBookmarkOverlay = () => ({
  type: ActionType.HIDE_BOOKMARK_OVERLAY,
})

/**
 * get bookmark details
 * @param type
 * @param ids
 * @param getParams
 * @returns {(function(*): Promise<void>)|*}
 */
export const fetchBookmarkDetails =
  (type, ids, getParams) => async (dispatch, getState) => {
    if (!!getState().apiError) {
      dispatch(hideAPIErrorInfo())
    }

    const params = getSearchGetParams({
      resultType: type,
      prevParams: getParams,
    })
    params.ids = ids.join(',') // as comma separated list instead of array
    params.size = ids.length // by default elasticsearch, searches return the top 10 matching hits

    let data = null
    await navigatorAPI
      .get(`${ apiEndpoints.bookmarks }${ type }/`, { params })
      .then((response) => {
        // add qsr identifiers (they where required for dynamic data rendering case: hospitals)
        if (type === entryPointId.HOSPITALS) {
          dispatch({
            type: ActionType.UPDATE_QSR_IDENTIFIER,
            payload: getQsrIdentifierArray(response.data.results),
          })
        }

        // handle success
        data = response.data
      })
      .catch((error) => {
        // handle error
        console.error('fetch details error: ', error)
        // only show error if request was not canceled
        if (!axios.isCancel(error)) {
          dispatch(showAPIErrorInfo())
        }
      })

    // sometimes the active_sorting is missing at the response
    if (data?.active_sorting === null) {
      data.active_sorting = 'alphabetical'
    }

    dispatch({
      type: ActionType.FETCH_BOOKMARKS_COMPLETE,
      payload: {
        selectedDetails: data,
        type,
        searchParams: getParams,
        items: ids,
      },
    })
  }
